import type { KeyboardEvent } from "react";
import { type ActivityEntry, isActivityTicket } from "../driver-activity-view-model";

export type UseActivityKeyboardOptions = {
  /** The rows in view, in order. */
  navigable: ActivityEntry[];
  activeKey?: string;
  setActiveKey: (key: string | undefined) => void;
  /** Scrolls a row into view, rendering its driver's card first if it was virtualized away. */
  revealEntry: (entry: ActivityEntry) => void;
  toggleSelected: (entry: ActivityEntry) => void;
  clearSelection: () => void;
  /** The element of a row, by entry, for anchoring its tools menu. */
  rowElement: (entry: ActivityEntry) => HTMLElement | null;
  openToolsMenu: (entry: ActivityEntry, anchor: HTMLElement) => void;
};

/**
 * The grid's keys: ↑ / ↓ move between rows, Space ticks a ticket, the context-menu key (or Shift+F10) opens
 * its tools, and Escape closes the open row, then clears the selection.
 */
export const activityKeyboardHandler = ({
  navigable,
  activeKey,
  setActiveKey,
  revealEntry,
  toggleSelected,
  clearSelection,
  rowElement,
  openToolsMenu,
}: UseActivityKeyboardOptions) => (event: KeyboardEvent<HTMLElement>) => {
  if (event.target !== event.currentTarget) return;
  const index = navigable.findIndex((entry) => entry.key === activeKey);

  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    const next = navigable[Math.min(Math.max(index + (event.key === "ArrowDown" ? 1 : -1), 0), navigable.length - 1)];
    if (!next) return;
    setActiveKey(next.key);
    revealEntry(next);
  } else if (event.key === " " && index >= 0 && isActivityTicket(navigable[index])) {
    event.preventDefault();
    toggleSelected(navigable[index]);
  } else if ((event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) && index >= 0) {
    const row = rowElement(navigable[index]);
    if (row && isActivityTicket(navigable[index])) {
      event.preventDefault();
      openToolsMenu(navigable[index], row);
    }
  } else if (event.key === "Escape") {
    if (activeKey) setActiveKey(undefined);
    else clearSelection();
  }
};
